import React, { useEffect, useRef } from 'react';
import './Sponsors.css';
import bauLogo from '../../photos/sponsors/bau.png';
import bahcesehirLogo from '../../photos/sponsors/bahcesehir.png';
import famaLogo from '../../photos/sponsors/fama.png';
import solidworksLogo from '../../photos/sponsors/solidworks.png';
import hidromarinLogo from '../../photos/sponsors/hidromarin.png';
import makerbeeLogo from '../../photos/sponsors/makerbee.png';
import tekyazLogo from '../../photos/sponsors/tekyaz.png';
import mgmLogo from '../../photos/sponsors/mgm.png';
import erdilKalipLogo from '../../photos/sponsors/erdil-kalip.png';
import boeingLogo from '../../photos/sponsors/boeing.png';
import bkSportsLogo from '../../photos/sponsors/bkspor.png';
import birikimKabloLogo from '../../photos/sponsors/birikimkablo.png';
import expoTechnicLogo from '../../photos/sponsors/expotechnic.jpg';
import globalParkLogo from '../../photos/sponsors/globalpark.jpeg';
import milaLogo from '../../photos/sponsors/mila.png';
import { Link } from 'react-router-dom';

const sponsors = [
  { name: 'Bah\u00e7e\u015fehir University', logo: bauLogo, tier: 'platinum' },
  { name: 'Bah\u00e7e\u015fehir Koleji', logo: bahcesehirLogo, tier: 'platinum' },
  { name: 'FAMA Transfer', logo: famaLogo, tier: 'platinum' },
  { name: 'SolidWorks', logo: solidworksLogo, tier: 'gold' },
  { name: 'MGM \u0130n\u015faat', logo: mgmLogo, tier: 'gold' },
  { name: 'Hidromarin', logo: hidromarinLogo, tier: 'gold' },
  { name: 'Boeing', logo: boeingLogo, tier: 'gold' },
  { name: 'Makerbee', logo: makerbeeLogo, tier: 'silver' },
  { name: 'Mila', logo: milaLogo, tier: 'silver' },
  { name: 'Tekyaz', logo: tekyazLogo, tier: 'silver' },
  { name: 'Global Park', logo: globalParkLogo, tier: 'silver' },
  { name: 'Expo Technic', logo: expoTechnicLogo, tier: 'bronze' },
  { name: 'Birikim Kablo', logo: birikimKabloLogo, tier: 'bronze' },
  { name: 'BK Sports', logo: bkSportsLogo, tier: 'bronze' },
  { name: 'Erdil Kal\u0131p', logo: erdilKalipLogo, tier: 'bronze' },
];

function Sponsors({ compact = false }) {
  const trackRef = useRef(null);
  const firstGroupRef = useRef(null);
  const tiers = ['platinum', 'gold', 'silver', 'bronze'];
  const compactMultiple = 3;
  const padCount = (compactMultiple - (sponsors.length % compactMultiple)) % compactMultiple;
  const marqueeSponsors = [...sponsors, ...sponsors.slice(0, padCount)];

  useEffect(() => {
    if (!compact) {
      return undefined;
    }

    const trackElement = trackRef.current;
    const firstGroupElement = firstGroupRef.current;
    if (!trackElement || !firstGroupElement) {
      return undefined;
    }

    let animationFrameId = 0;
    let resizeObserver = null;
    let lastTimestamp = 0;
    let offset = 0;
    let groupWidth = firstGroupElement.getBoundingClientRect().width;
    const prefersReducedMotion = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const speed = prefersReducedMotion ? 18 : 52;

    const setTransform = () => {
      trackElement.style.transform = `translate3d(${offset}px, 0, 0)`;
    };

    const updateWidth = () => {
      groupWidth = firstGroupElement.getBoundingClientRect().width;
      if (groupWidth > 0 && offset <= -groupWidth) {
        offset %= groupWidth;
        setTransform();
      }
    };

    const step = (timestamp) => {
      if (!lastTimestamp) {
        lastTimestamp = timestamp;
      }

      const delta = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      if (groupWidth > 0) {
        offset -= (speed * delta) / 1000;
        if (offset <= -groupWidth) {
          offset += groupWidth;
        }
        setTransform();
      }

      animationFrameId = window.requestAnimationFrame(step);
    };

    updateWidth();
    setTransform();
    animationFrameId = window.requestAnimationFrame(step);

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(firstGroupElement);
    } else {
      window.addEventListener('resize', updateWidth);
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateWidth);
      }
    };
  }, [compact]);

  const renderSponsorCard = (sponsor, index, suffix = '') => (
    <div
      key={`${sponsor.name}-${suffix}-${index}`}
      className={`sponsor-card ${sponsor.tier}`}
      style={{ '--animation-order': index }}
    >
      <div className="sponsor-card-inner">
        <div className="sponsor-logo-wrapper">
          <img src={sponsor.logo} alt={`${sponsor.name} logo`} loading="lazy" />
        </div>
        <span className="sponsor-name">{sponsor.name}</span>
      </div>
    </div>
  );

  const renderSponsorsGrid = (tierSponsors) => (
    <div className="sponsors-grid">
      {tierSponsors.map((sponsor, index) => renderSponsorCard(sponsor, index, 'grid'))}
    </div>
  );

  function SponsorSlideshow() {
    return (
      <div className="sponsors-slideshow" aria-label="Partner logos">
        <div className="sponsors-track" ref={trackRef}>
          {[0, 1].map((groupIndex) => (
            <div
              key={groupIndex}
              className="sponsors-marquee-group"
              ref={groupIndex === 0 ? firstGroupRef : undefined}
              aria-hidden={groupIndex === 1 || undefined}
            >
              {marqueeSponsors.map((sponsor, index) => (
                <div
                  key={`${groupIndex}-${sponsor.name}-${index}`}
                  className={`sponsor-card ${sponsor.tier} compact`}
                >
                  <div className="sponsor-card-inner">
                    <div className="sponsor-logo-wrapper">
                      <img src={sponsor.logo} alt={`${sponsor.name} logo`} loading="lazy" />
                    </div>
                    <span className="sponsor-name">{sponsor.name}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="sponsors-container compact">
        <div className="sponsors-showcase">
          <h2 className="section-title fade-in">Our Valued Partners</h2>
          <SponsorSlideshow />
          <div className="view-all-wrapper fade-in">
            <Link to="/sponsors" className="view-all-sponsors">
              View Partners
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sponsors-container">
      <div className="sponsors-header">
        <div className="sponsors-header-copy">
          <h1 className="section-title fade-in">Our Valued Partners</h1>
          <p className="sponsors-intro fade-in">
            We are proud to collaborate with industry leaders who share our vision of advancing robotics and STEM
            education. Their support empowers the next generation of innovators.
          </p>
        </div>
      </div>
      <div className="sponsors-tiers">
        {tiers.map((tier, tierIndex) => {
          const tierSponsors = sponsors.filter((sponsor) => sponsor.tier === tier);
          if (tierSponsors.length === 0) {
            return null;
          }

          return (
            <div
              key={tier}
              className={`tier-section ${tier} fade-in`}
              style={{ '--animation-delay': `${0.3 + tierIndex * 0.15}s` }}
            >
              <div className="tier-heading">
                <h2 className="tier-title tier-title-centered">{`${tier.charAt(0).toUpperCase()}${tier.slice(1)} Sponsors`}</h2>
              </div>
              {renderSponsorsGrid(tierSponsors)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Sponsors;
